import { useAuth } from '@/providers/Auth';
import {
  LogoutOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Breadcrumb, Dropdown, Layout, MenuProps, Tag } from 'antd';
import { useLocation } from 'react-router-dom';
import { ADMIN_ONLY, hasAnyPermission } from '@/utils/permission';
import { findLeafLabelForPath, navGroups } from '../Sidebar/menuItems';

type Props = {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  isMobile: boolean;
  onOpenMobile: () => void;
};

export const AppHeader: React.FC<Props> = ({
  collapsed,
  onToggleCollapsed,
  isMobile,
  onOpenMobile,
}) => {
  const location = useLocation();
  const { logOutSuccess, currentUser } = useAuth();
  // currentUser の復元前は従来どおり管理者表示（閲覧専用ロールだと確定してから切り替える）
  const isAdmin = !currentUser || hasAnyPermission(currentUser, ADMIN_ONLY);
  const roleLabel = isAdmin ? 'システム管理者' : 'ダッシュボード閲覧';

  const { group, leaf } = findLeafLabelForPath(navGroups, location.pathname);

  const userMenu: MenuProps['items'] = [
    {
      key: 'user-info',
      label: (
        <div>
          <Tag>{isAdmin ? '管理者' : '閲覧専用'}</Tag>
          <div className='mt-xs font-semibold'>{roleLabel}</div>
        </div>
      ),
    },
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'ログアウト',
      onClick: logOutSuccess,
    },
  ];

  const ToggleIcon = isMobile
    ? MenuOutlined
    : collapsed
      ? MenuUnfoldOutlined
      : MenuFoldOutlined;

  return (
    <Layout.Header
      style={{
        background: 'var(--color-surface-base)',
        borderBlockEnd: '1px solid var(--color-line-subtle)',
        padding: 0,
        height: 48,
        lineHeight: '48px',
      }}
      className='flex items-center justify-between px-md sticky top-0 z-10'
    >
      <div className='flex items-center gap-md'>
        <button
          type='button'
          onClick={isMobile ? onOpenMobile : onToggleCollapsed}
          className='h-8 w-8 flex items-center justify-center rounded hover:bg-surface text-fg-muted'
          aria-label='toggle navigation'
        >
          <ToggleIcon style={{ fontSize: 16 }} />
        </button>
        {(group || leaf) && (
          <Breadcrumb
            items={[
              ...(group ? [{ title: group }] : []),
              ...(leaf ? [{ title: <span className='text-fg'>{leaf}</span> }] : []),
            ]}
          />
        )}
      </div>

      <Dropdown menu={{ items: userMenu }} placement='bottomRight' trigger={['click']}>
        <div className='flex items-center gap-sm cursor-pointer px-md h-full hover:bg-surface'>
          <UserOutlined className='text-fg-muted' />
          <span className='text-fg text-[13px] leading-none'>{roleLabel}</span>
        </div>
      </Dropdown>
    </Layout.Header>
  );
};
